import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, Typography, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle } from '@mui/material';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import MockTestShareButton from '../Common/MockTestShareButton';

const FULL_TEST_MODE_KEY = 'ielts-full-test-mode';
const SIMULATION_WARNING = 'Choose Simulation only when you are ready for the full timed test. In Simulation mode, Listening, Reading, and Writing cannot be submitted before the time finishes. In Listening, audio cannot be paused, replayed, skipped, or moved from the bar. If you may need to submit early or control audio, choose Practice mode.';

const CardContainer = styled(Box)(({ theme }) => ({
  border: '1px solid #EDF2F7',
  borderRadius: '12px',
  padding: '16px 20px',
  width: '100%',
  marginBottom: '12px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: '#FFFFFF',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)',
  transition: 'all 0.2s ease',
  boxSizing: 'border-box',
  '&:hover': {
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.04)',
    borderColor: '#E2E8F0',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '12px 16px',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '12px',
  },
}));

const TestInfo = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
});

const TestTitle = styled(Typography)({
  fontSize: '15px',
  fontWeight: 700,
  color: '#2D3748',
  marginBottom: '4px',
  fontFamily: "'Poppins', 'Inter', sans-serif",
});

const TestTime = styled(Typography)({
  fontSize: '13px',
  fontWeight: 600,
  color: '#718096',
  fontFamily: "'Poppins', 'Inter', sans-serif",
});

const DifficultyChip = styled(Chip)(({ level }) => ({
  backgroundColor: level === 'Easy' ? '#E6FFFA' : level === 'Medium' ? '#FFFBEB' : '#FFF5F5',
  color: level === 'Easy' ? '#319795' : level === 'Medium' ? '#D69E2E' : '#E53E3E',
  height: '22px',
  fontSize: '11px',
  fontWeight: 800,
  borderRadius: '6px',
  marginRight: '12px',
  fontFamily: "'Poppins', 'Inter', sans-serif",
  '& .MuiChip-label': {
    padding: '0 8px',
  },
}));

const SimulationBadge = styled(Chip)({
  height: '22px',
  borderRadius: '6px',
  backgroundColor: '#EEF7FF',
  color: '#175CD3',
  border: '1px solid #B2DDFF',
  fontSize: '10px',
  fontWeight: 800,
  fontFamily: "'Poppins', 'Inter', sans-serif",
  '& .MuiChip-label': {
    padding: '0 8px',
  },
});

const StartButton = styled(Button)(({ theme, isLocked }) => ({
  backgroundColor: isLocked ? '#C6CBD9' : '#996CFE',
  color: 'white',
  borderRadius: '10px',
  padding: '6px 20px',
  fontSize: '13px',
  fontWeight: 800,
  textTransform: 'uppercase',
  minWidth: '90px',
  height: '38px',
  boxShadow: isLocked ? 'none' : '0 4px 6px rgba(153, 108, 254, 0.15)',
  fontFamily: "'Poppins', 'Inter', sans-serif",
  cursor: isLocked ? 'not-allowed' : 'pointer',
  '&:hover': {
    backgroundColor: isLocked ? '#C6CBD9' : '#7C4DFF',
    boxShadow: isLocked ? 'none' : '0 6px 12px rgba(153, 108, 254, 0.25)',
  },
  [theme.breakpoints.down('sm')]: {
    minWidth: '70px',
    padding: '4px 12px',
  },
}));

const OutlinedStartButton = styled(Button)(({ theme, isLocked }) => ({
  borderRadius: '10px',
  padding: '6px 20px',
  fontSize: '13px',
  fontWeight: 800,
  textTransform: 'uppercase',
  minWidth: '90px',
  height: '38px',
  fontFamily: "'Poppins', 'Inter', sans-serif",
  borderColor: isLocked ? '#C6CBD9' : '#996CFE',
  color: isLocked ? '#A0AEC0' : '#996CFE',
  backgroundColor: '#fff',
  cursor: isLocked ? 'not-allowed' : 'pointer',
  '&:hover': {
    borderColor: isLocked ? '#C6CBD9' : '#7C4DFF',
    backgroundColor: isLocked ? '#fff' : 'rgba(153, 108, 254, 0.06)',
  },
  [theme.breakpoints.down('sm')]: {
    minWidth: '70px',
    padding: '4px 12px',
  },
}));

const ActionsWrap = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '8px',
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    justifyContent: 'flex-end',
  },
}));

const ModeGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: '16px',
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

const ModeChoice = styled(Button)(({ theme }) => ({
  border: '1px solid #E6E0F5',
  borderRadius: '14px',
  padding: '18px',
  textTransform: 'none',
  color: '#2D3748',
  alignItems: 'stretch',
  justifyContent: 'flex-start',
  background: '#fff',
  minHeight: '260px',
  '&:hover': {
    borderColor: '#996CFE',
    background: '#FBF8FF',
  },
}));

const ModeIconCircle = styled(Box)({
  width: 64,
  height: 64,
  borderRadius: '50%',
  background: '#F8F2FF',
  color: '#996CFE',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 12px',
});

const WarningNote = styled(Typography)({
  color: '#B42318',
  backgroundColor: '#FFF1F0',
  border: '1px solid #FDA29B',
  borderRadius: '8px',
  fontSize: '12px',
  fontWeight: 700,
  lineHeight: 1.45,
  padding: '9px 10px',
  marginTop: '12px',
});

const TestCard = ({
  title,
  time,
  difficulty,
  onStart,
  onResume,
  showResumeButton = false,
  loading = false,
  loadingStart = false,
  loadingResume = false,
  isLocked = false,
  canRestart = true,
  disabledMessage = null,
  startLabel = 'Start',
  showSpeakingSimulationBadge = false,
  sharePath,
}) => {
  const [modeDialogOpen, setModeDialogOpen] = React.useState(false);
  const isDisabled = isLocked || !!disabledMessage;
  const startPending = showResumeButton ? loadingStart : loadingStart || loading;
  const resumePending = loadingResume;

  const handleStartClick = () => {
    if (isDisabled || startPending || resumePending) return;
    setModeDialogOpen(true);
  };

  const handleModeStart = (mode) => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(FULL_TEST_MODE_KEY, mode);
    }
    setModeDialogOpen(false);
    if (onStart) onStart(mode);
  };
  
  return (
    <>
      <CardContainer className="ielts-sectional-test-card">
        <TestInfo className="ielts-sectional-test-info">
          <Box display="flex" alignItems="center" mb={0.5}>
            <TestTitle>{title}</TestTitle>
          </Box>
          <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
            <DifficultyChip label={difficulty} level={difficulty} size="small" />
            <TestTime>{time}</TestTime>
            {title.includes('Complete IELTS Test') && (
              <Chip 
                label="Speaking Optional" 
                size="small" 
                variant="outlined"
                sx={{ 
                  fontSize: '10px', 
                  height: '20px',
                  color: '#996CFE',
                  borderColor: '#996CFE'
                }} 
              />
            )}
            {showSpeakingSimulationBadge && (
              <SimulationBadge label="Speaking Simulation" size="small" />
            )}
          </Box>
          {disabledMessage && (
            <Typography 
              sx={{ 
                fontSize: '11px', 
                color: '#E53E3E', 
                fontWeight: 600,
                mt: 0.5,
                fontFamily: "'Poppins', 'Inter', sans-serif"
              }}
            >
              {disabledMessage}
            </Typography>
          )}
        </TestInfo>
        {showResumeButton && onResume ? (
          <ActionsWrap className="ielts-sectional-test-actions">
            <MockTestShareButton path={sharePath} />
            {canRestart && <OutlinedStartButton
              variant="outlined"
              onClick={isDisabled ? undefined : handleStartClick}
              disabled={startPending || resumePending || isDisabled}
              isLocked={isDisabled}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                {startPending ? (
                  <CircularProgress size={16} sx={{ color: '#996CFE' }} />
                ) : (
                  isDisabled ? (isLocked ? 'Locked' : 'Disabled') : startLabel
                )}
              </Box>
            </OutlinedStartButton>}
            <StartButton
              onClick={isDisabled ? undefined : onResume}
              disabled={startPending || resumePending || isDisabled}
              isLocked={isDisabled}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                {resumePending ? (
                  <CircularProgress size={16} sx={{ color: 'white' }} />
                ) : (
                  'Resume'
                )}
              </Box>
            </StartButton>
          </ActionsWrap>
        ) : (
          <ActionsWrap className="ielts-sectional-test-actions">
            <MockTestShareButton path={sharePath} />
            <StartButton onClick={isDisabled ? undefined : handleStartClick} disabled={startPending || isDisabled} isLocked={isDisabled}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                {startPending ? (
                  <CircularProgress
                    size={16}
                    sx={{ color: 'white' }}
                  />
                ) : (
                  isDisabled ? (isLocked ? 'Locked' : 'Disabled') : startLabel
                )}
              </Box>
            </StartButton>
          </ActionsWrap>
        )}
      </CardContainer>

      <Dialog open={modeDialogOpen} onClose={() => setModeDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ textAlign: 'center', color: '#996CFE', fontWeight: 800 }}>
          Choose Test Mode
        </DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
          <ModeGrid>
            <ModeChoice onClick={() => handleModeStart('practice')}>
              <Box sx={{ width: '100%', textAlign: 'center' }}>
                <ModeIconCircle>
                  <AssignmentOutlinedIcon sx={{ fontSize: 34 }} />
                </ModeIconCircle>
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                  Practice mode
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  Choose Practice if you want more flexibility while taking the full mock test.
                </Typography>
              </Box>
            </ModeChoice>

            <ModeChoice onClick={() => handleModeStart('simulation')}>
              <Box sx={{ width: '100%', textAlign: 'center' }}>
                <ModeIconCircle>
                  <TimerOutlinedIcon sx={{ fontSize: 34 }} />
                </ModeIconCircle>
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                  Simulation test mode
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  Choose Simulation to experience the real IELTS full test flow.
                </Typography>
                <WarningNote>{SIMULATION_WARNING}</WarningNote>
              </Box>
            </ModeChoice>
          </ModeGrid>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TestCard; 
